import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Phone normalization (mirrors backend logic) ──
export function normalizePhone(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '').replace(/^0+/, '');
  if (digits.startsWith('91') && digits.length === 12) return '+' + digits;
  if (digits.length === 10) return '+91' + digits;
  if (digits.startsWith('91') && digits.length > 12) return '+91' + digits.slice(-10);
  if (digits.length > 10) return '+91' + digits.slice(-10);
  return '+' + digits;
}

const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  deleteAccount: () => api.delete('/auth/delete-account'),
};

export const userAPI = {
  getUsers: () => api.get('/users'),
  syncContacts: (contacts) => api.post('/users/sync-contacts', {
    contacts: contacts.map(p => normalizePhone(p)).filter(Boolean),
  }),
  addContact: (phone) => api.post('/users/add-contact', { phone: normalizePhone(phone) }),
  updatePhone: (phone) => api.post('/users/phone', { phone: normalizePhone(phone) }),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/users/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAvatarUrl: (avatarUrl) => avatarUrl ? `${API}${avatarUrl}` : null,
};

export const searchAPI = {
  searchMessages: (query) => api.get('/search/messages', { params: { q: query } }),
};

export const channelAPI = {
  createChannel: (data) => api.post('/channels', data),
  getChannels: () => api.get('/channels'),
  getChannel: (id) => api.get(`/channels/${id}`),
};

export const messageAPI = {
  createMessage: (data) => api.post('/messages', data),
  getMessages: (channelId) => api.get(`/messages/${channelId}`),
  addReaction: (messageId, emoji) => api.post(`/messages/${messageId}/reactions`, null, { params: { emoji } }),
  removeReaction: (messageId, emoji) => api.delete(`/messages/${messageId}/reactions`, { params: { emoji } }),
  forwardMessage: (messageId, targetChannelIds) => api.post('/messages/forward', { message_id: messageId, target_channel_ids: targetChannelIds }),
  deleteForEveryone: (messageId) => api.delete(`/messages/${messageId}`),
  deleteForMe: (messageId) => api.post(`/messages/${messageId}/delete-for-me`),
};

export const fileAPI = {
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getFileUrl: (fileId) => `${API}/files/${fileId}?auth=${localStorage.getItem('token')}`,
};

export const agoraAPI = {
  getToken: (channelName, uid) => api.post('/agora/token', { channel_name: channelName, uid }),
};

export const e2eeAPI = {
  uploadPublicKey: (publicKey) => api.post('/e2ee/keys', { public_key: publicKey }),
  getPublicKey: (userId) => api.get(`/e2ee/keys/${userId}`),
  getChannelKeys: (channelId) => api.get(`/e2ee/channel-keys/${channelId}`),
};

export const meetingAPI = {
  createMeeting: (data) => api.post('/meetings', data),
  getMeetings: () => api.get('/meetings'),
  getMeeting: (meetingId) => api.get(`/meetings/${meetingId}`),
  getMeetingByCode: (joinCode) => api.get(`/meetings/join/${joinCode}`),
  joinMeeting: (joinCode) => api.post(`/meetings/join/${joinCode}`),
  startMeeting: (meetingId) => api.patch(`/meetings/${meetingId}/start`),
  endMeeting: (meetingId) => api.patch(`/meetings/${meetingId}/end`),
  leaveMeeting: (meetingId) => api.post(`/meetings/${meetingId}/leave`),
  kickParticipant: (meetingId, userId) => api.post(`/meetings/${meetingId}/kick/${userId}`),
  hostAction: (meetingId, action, targetUserId) => api.post(`/meetings/${meetingId}/host-action`, null, { params: { action, target_user_id: targetUserId } }),
  getMeetingToken: (meetingId, uid) => api.post(`/meetings/${meetingId}/token`, null, { params: { uid } }),
  getParticipantsMap: (meetingId) => api.get(`/meetings/${meetingId}/participants-map`),
};

export const aiAPI = {
  summarizeChat: (channelId) => api.post('/ai/summarize', null, { params: { channel_id: channelId } }),
  suggestReply: (channelId) => api.post('/ai/suggest-reply', null, { params: { channel_id: channelId } }),
  meetingNotes: (channelId) => api.post('/ai/meeting-notes', null, { params: { channel_id: channelId } }),
  getNotes: (channelId) => api.get(`/ai/notes/${channelId}`),
};

export const videoAPI = {
  getCredits: () => api.get('/ai/video/credits'),
  generate: (data) => api.post('/ai/video/generate', data),
  getStatus: (videoId) => api.get(`/ai/video/${videoId}/status`),
  getHistory: () => api.get('/ai/video/history'),
  deleteVideo: (videoId) => api.delete(`/ai/video/${videoId}`),
  getGallery: (page = 1, limit = 10) => api.get(`/ai/video/gallery?page=${page}&limit=${limit}`),
  toggleLike: (videoId) => api.post(`/ai/video/${videoId}/like`),
  toggleVisibility: (videoId) => api.put(`/ai/video/${videoId}/visibility`),
  getLeaderboard: () => api.get('/ai/video/leaderboard'),
  getTrending: () => api.get('/ai/video/trending'),
};

export const paymentAPI = {
  getPacks: () => api.get('/payments/packs'),
  createOrder: (packId) => api.post('/payments/create-order', { pack_id: packId }),
  verifyPayment: (orderId, paymentId, signature) =>
    api.post(`/payments/verify?razorpay_order_id=${orderId}&razorpay_payment_id=${paymentId}&razorpay_signature=${signature}`),
};

export const statusAPI = {
  createStatus: (data) => api.post('/statuses', data),
  getStatuses: () => api.get('/statuses'),
  viewStatus: (statusId) => api.post(`/statuses/${statusId}/view`),
  deleteStatus: (statusId) => api.delete(`/statuses/${statusId}`),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: () => api.get('/admin/users'),
  getChannels: () => api.get('/admin/channels'),
  getMeetings: () => api.get('/admin/meetings'),
  getLive: () => api.get('/admin/live'),
  banUser: (userId) => api.put(`/admin/users/${userId}/ban`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  deleteChannel: (channelId) => api.delete(`/admin/channels/${channelId}`),
  forceEndMeeting: (meetingId) => api.post(`/admin/meetings/${meetingId}/end`),
};

export const voiceAPI = {
  uploadAudio: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload-audio', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export default api;
